import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  RiFileListLine,
  RiSendPlaneFill,
  RiAwardLine,
  RiMailLine,
  RiPhoneLine,
} from "react-icons/ri";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import { API_BASE } from "../config";

const T = {
  white: "#FFFFFF",
  offWhite: "#F8FAFC",
  softBlue: "#EFF6FF",
  primary: "#1A56DB",
  primaryDark: "#1240A8",
  primaryLight: "#3B82F6",
  navy: "#0F2557",
  text: "#1E293B",
  textMuted: "#64748B",
  border: "#E2E8F0",
  borderLight: "#F1F5F9",
  shadow: "0 4px 20px rgba(0,0,0,0.05)",
  shadowMd: "0 8px 30px rgba(0,0,0,0.08)",
  gradPrimary: "linear-gradient(135deg, #1A56DB 0%, #3B82F6 100%)",
  gradNavy: "linear-gradient(135deg, #0F2557 0%, #1A56DB 100%)",
};

export default function Abstract() {
  const navigate = useNavigate();
  const [guidelines, setGuidelines] = useState([]);
  const [guidelinesTitle, setGuidelinesTitle] = useState(
    "Submission Guidelines",
  );
  const [guidelinesDescription, setGuidelinesDescription] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGuidelines = async () => {
      try {
        console.log("Fetching abstract guidelines...");
        const response = await axios.get(
          `${API_BASE}/api/abstract-guidelines/active`,
        );
        console.log("API Response:", response.data);

        if (response.data.success && response.data.data) {
          const data = response.data.data;
          setGuidelinesTitle(data.title || "Submission Guidelines");
          setGuidelinesDescription(data.description || "");
          setGuidelines(data.guidelines || []);
        } else {
          console.log("No active guidelines found");
        }
      } catch (error) {
        console.error("Error fetching guidelines:", error);
        toast.error("Failed to load guidelines");
      } finally {
        setLoading(false);
      }
    };

    fetchGuidelines();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: T.offWhite,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: `3px solid ${T.border}`,
            borderTop: `3px solid ${T.primary}`,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: T.offWhite }}>
      <Toaster position="top-right" />
      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>

      {/* Hero Section */}
      <section
        style={{
          textAlign: "center",
          padding: "80px 24px 60px",
          background: T.gradNavy,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            background: "rgba(255,255,255,0.12)",
            borderRadius: 60,
            padding: "6px 20px",
            marginBottom: 28,
          }}
        >
          <RiAwardLine size={14} color="#60A5FA" />
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#fff",
              textTransform: "uppercase",
            }}
          >
            Call for Abstracts
          </span>
        </div>
        <h1
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(48px, 7vw, 80px)",
            fontWeight: 900,
            color: "#fff",
            marginBottom: 20,
          }}
        >
          Abstract{" "}
          <span
            style={{
              background: T.gradPrimary,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Guidelines
          </span>
        </h1>
        <p
          style={{
            color: "#60A5FA",
            fontSize: 18,
            maxWidth: 750,
            margin: "0 auto",
          }}
        >
          Submit your groundbreaking research for Immunology Summit 2026
        </p>
      </section>

      {/* Main Content */}
      <div
        style={{ maxWidth: 1000, margin: "0 auto", padding: "20px 48px 80px" }}
      >
        {/* Guidelines Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: T.white,
            borderRadius: 24,
            border: `1px solid ${T.borderLight}`,
            padding: "40px",
            boxShadow: T.shadowMd,
            marginBottom: 48,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: "50%",
                background: T.softBlue,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <RiFileListLine size={28} color={T.primary} />
            </div>
            <div>
              <h2 style={{ fontSize: 28, fontWeight: 800, color: T.text }}>
                {guidelinesTitle}
              </h2>
              {guidelinesDescription && (
                <p style={{ fontSize: 14, color: T.textMuted, marginTop: 4 }}>
                  {guidelinesDescription}
                </p>
              )}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {guidelines.length > 0 ? (
              guidelines
                .sort((a, b) => a.order - b.order)
                .map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      gap: 16,
                      paddingBottom: 16,
                      borderBottom:
                        idx < guidelines.length - 1
                          ? `1px solid ${T.borderLight}`
                          : "none",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 32,
                        minWidth: 50,
                        textAlign: "center",
                      }}
                    >
                      {item.icon || "📝"}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: T.text,
                          marginBottom: 8,
                        }}
                      >
                        {item.title}
                      </h3>
                      <p
                        style={{
                          fontSize: 15,
                          color: T.textMuted,
                          lineHeight: 1.5,
                        }}
                      >
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: T.textMuted,
                }}
              >
                No guidelines available at the moment. Please check back later.
              </div>
            )}
          </div>
        </motion.div>

        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={{ textAlign: "center" }}
        >
          <button
            onClick={() => navigate("/abstract-submission")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              background: T.gradPrimary,
              color: T.white,
              padding: "16px 48px",
              borderRadius: 50,
              fontSize: 18,
              fontWeight: 700,
              border: "none",
              cursor: "pointer",
              transition: "transform 0.2s, box-shadow 0.2s",
              boxShadow: T.shadow,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-3px)";
              e.currentTarget.style.boxShadow = T.shadowMd;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = T.shadow;
            }}
          >
            <RiSendPlaneFill size={20} />
            Submit Abstract
          </button>
        </motion.div>
      </div>

      {/* Footer */}
      <footer
        style={{
          background: T.white,
          borderTop: `1px solid ${T.borderLight}`,
          padding: "48px",
          textAlign: "center",
        }}
      >
        <p style={{ color: T.textMuted, fontSize: 13 }}>
          © 2025 Immunology Summit. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
